import { z } from "zod";

import { maybe, parser, type Parser } from "../../identity/live/schemas";
import type { DirectoryEntry, Issued, Received, Register, Transmittal } from "./types";

/** The register as the identity service sends it; `null` becomes `undefined`. */

const status = z.enum(["current", "superseded", "void"]);
const delivery = z.enum(["in_app", "link", "by_hand"]);
const recipientStatus = z.enum(["sent", "opened", "acknowledged"]);

const issueRef = z.object({
  transmittalId: z.string(),
  number: z.string(),
  purpose: z.string(),
  purposeLabel: z.string(),
  issuedAt: z.string(),
});

const documentSchema = z.object({
  id: z.string(),
  number: z.string(),
  title: z.string(),
  kind: z.enum(["drawing", "layout", "sheet", "document"]),
  discipline: maybe(z.string()),
  collection: maybe(z.string()),
  size: maybe(z.string()),
  scale: maybe(z.string()),
  stageId: maybe(z.string()),
  drawing: maybe(z.string()),
  revision: maybe(z.string()),
  revisionDay: maybe(z.string()),
  status: maybe(status),
  purpose: maybe(z.string()),
  purposeLabel: maybe(z.string()),
  latestIssue: maybe(
    issueRef.extend({ revision: z.string(), recipients: z.number(), acknowledged: z.number() }),
  ),
  revisions: z.array(
    z.object({
      id: z.string(),
      revision: z.string(),
      day: z.string(),
      description: z.string(),
      drawingVersion: maybe(z.number()),
      fileHash: maybe(z.string()),
      status,
      issues: z.array(issueRef),
    }),
  ),
});

const registerSchema = z.object({
  documents: z.array(documentSchema),
  purposes: z.array(z.object({ code: z.string(), label: z.string() })),
});

const entrySchema = z.object({
  id: z.string(),
  kind: z.enum(["company", "person"]),
  name: z.string(),
  company: maybe(z.string()),
  trade: maybe(z.string()),
  role: z.enum(["client", "consultant", "contractor", "vendor", "other"]),
  email: maybe(z.string()),
  phone: maybe(z.string()),
  hasAccount: z.boolean(),
});

const transmittalSchema = z.object({
  id: z.string(),
  seq: z.number(),
  number: z.string(),
  purpose: z.string(),
  purposeLabel: z.string(),
  subject: z.string(),
  coverNote: z.string(),
  issuedBy: z.string(),
  issuedAt: z.string(),
  items: z.array(
    z.object({
      documentId: z.string(),
      number: z.string(),
      title: z.string(),
      revision: z.string(),
      revisionDay: z.string(),
      fileHash: maybe(z.string()),
    }),
  ),
  recipients: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      company: maybe(z.string()),
      email: maybe(z.string()),
      role: z.string(),
      delivery,
      status: recipientStatus,
      openedAt: maybe(z.string()),
      acknowledgedAt: maybe(z.string()),
      acknowledgedBy: maybe(z.string()),
      note: maybe(z.string()),
      overdue: z.boolean(),
    }),
  ),
  acknowledged: z.number(),
});

export const receivedSchema = z.object({
  recipientId: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  firmName: z.string(),
  number: z.string(),
  purposeLabel: z.string(),
  subject: z.string(),
  coverNote: z.string(),
  issuedBy: z.string(),
  issuedAt: z.string(),
  items: z.array(
    z.object({
      number: z.string(),
      title: z.string(),
      revision: z.string(),
      revisionDay: z.string(),
    }),
  ),
  status: recipientStatus,
  acknowledgedAt: maybe(z.string()),
  note: maybe(z.string()),
});

export const parseRegister: Parser<Register> = parser(registerSchema);
export const parseDirectory: Parser<DirectoryEntry[]> = parser(z.array(entrySchema));
export const parseTransmittals: Parser<Transmittal[]> = parser(z.array(transmittalSchema));
export const parseTransmittal: Parser<Transmittal> = parser(transmittalSchema);
export const parseIssued: Parser<Issued> = parser(
  z.object({
    transmittal: transmittalSchema,
    links: z.array(z.object({ recipientId: z.string(), url: z.string() })),
  }),
);
export const parseReceived: Parser<Received> = parser(receivedSchema);
export const parseReceivedList: Parser<Received[]> = parser(z.array(receivedSchema));
